import { useState } from 'react';
import { Container, DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { IconImageModal } from './IconImageModal';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import {
  IconImageStatus,
  IIconImageData,
  IIconImageFormData,
} from '@/types/icon-images-management.types';
import {
  useGetIconImagesQuery,
  useUpdateIconImageMutation,
  useCreateIconImageMutation,
  useUpdateIconLifecycleMutation,
  useDeleteIconImageMutation,
} from '@/services/IconImagesManagementService';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { ColumnDef } from '@tanstack/react-table';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';

const IconImagesListings = () => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    image: IIconImageData | null;
    mode: 'edit' | 'create';
  }>({ isOpen: false, image: null, mode: 'edit' });

  const [formData, setFormData] = useState<IIconImageFormData>({
    name: '',
    displayName: '',
    file: null,
    lang: 'en',
    fileUrl: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<IIconImageData | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [updateIconImage] = useUpdateIconImageMutation();
  const [createIconImage] = useCreateIconImageMutation();
  const [updateIconLifecycle] = useUpdateIconLifecycleMutation();
  const [deleteIconImage, { isLoading: isDeleting }] = useDeleteIconImageMutation();
  const {
    data: iconImages,
    isLoading,
    isError,
    refetch,
  } = useGetIconImagesQuery({
    lang: 'en',
  });

  const getDisplayName = (image: IIconImageData) => image.title ?? image.name;

  const getUsageLabel = (image: IIconImageData) => {
    if (image.usageCount > 0) {
      return `In use (${image.usageCount})`;
    }
    return 'Not referenced';
  };

  const handleOpenEditModal = (image: IIconImageData) => {
    const displayValue = getDisplayName(image);
    setModalState({ isOpen: true, image, mode: 'edit' });
    setFormData({
      displayName: displayValue,
      originalName: displayValue,
      file: null,
      lang: 'en',
      fileUrl: image.imageUrl,
    });
    setErrors({});
  };

  const handleOpenCreateModal = () => {
    setModalState({ isOpen: true, image: null, mode: 'create' });
    setFormData({ name: '', displayName: '', file: null, lang: 'en', fileUrl: '' });
    setErrors({});
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, image: null, mode: 'edit' });
    setFormData({ name: '', displayName: '', file: null, lang: 'en', fileUrl: '' });
    setErrors({});
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (modalState.mode === 'create') {
      try {
        const trimmedTitle = formData.displayName.trim();
        await createIconImage({
          name: (formData.name ?? '').trim(),
          ...(trimmedTitle ? { title: trimmedTitle } : {}),
          file: formData.file as File,
          lang: formData.lang,
        }).unwrap();

        enqueueSnackbar('Icon created successfully', { variant: 'success' });
        handleCloseModal();
        refetch();
      } catch (err) {
        const apiErr = err as { data?: { message?: string } };
        const message = apiErr?.data?.message ?? 'Failed to create icon';
        setErrors((prev) => ({ ...prev, name: message }));
        enqueueSnackbar(message, { variant: 'error' });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!modalState.image) {
      setIsSubmitting(false);
      return;
    }

    const trimmedTitle = formData.displayName.trim();
    const originalTitle = (
      formData.originalName ?? getDisplayName(modalState.image)
    ).trim();
    const titleChanged = trimmedTitle !== originalTitle;

    try {
      await updateIconImage({
        id: modalState.image.id,
        ...(titleChanged ? { title: trimmedTitle } : {}),
        ...(formData.file ? { file: formData.file } : {}),
        lang: formData.lang,
      }).unwrap();

      enqueueSnackbar('Icon updated successfully', {
        variant: 'success',
      });
      handleCloseModal();
      refetch();
    } catch (err) {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(apiErr?.data?.message ?? 'Failed to update icon', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLifecycleAction = async (
    image: IIconImageData,
    action: 'retire' | 'reactivate'
  ) => {
    try {
      await updateIconLifecycle({
        id: image.id,
        action,
        lang: 'en',
      }).unwrap();
      enqueueSnackbar(
        action === 'retire'
          ? 'Icon retired successfully'
          : 'Icon reactivated successfully',
        { variant: 'success' }
      );
      refetch();
    } catch (err) {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ??
          (action === 'retire'
            ? 'Failed to retire icon'
            : 'Failed to reactivate icon'),
        { variant: 'error' }
      );
    }
  };

  const handleDeleteClick = (image: IIconImageData) => {
    if (!image.canDelete) {
      enqueueSnackbar(
        'This icon is still referenced and cannot be permanently deleted',
        { variant: 'error' }
      );
      return;
    }
    setImageToDelete(image);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;

    try {
      await deleteIconImage({ id: imageToDelete.id, lang: 'en' }).unwrap();
      enqueueSnackbar('Icon permanently deleted', { variant: 'success' });
      setIsDeleteModalOpen(false);
      setImageToDelete(null);
      refetch();
    } catch (err) {
      const apiErr = err as { data?: { message?: string } };
      enqueueSnackbar(
        apiErr?.data?.message ?? 'Failed to delete icon',
        { variant: 'error' }
      );
    }
  };

  const columns: ColumnDef<IIconImageData>[] = [
    {
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => row.original.id,
    },
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <div>{getDisplayName(row.original)}</div>
          <div className="text-xs text-gray-500">{row.original.name}</div>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => (
        <span
          className={
            row.original.status === IconImageStatus.RETIRED
              ? 'text-amber-700 contrast:text-amber-300'
              : 'text-green-700 contrast:text-green-300'
          }
        >
          {row.original.status === IconImageStatus.RETIRED
            ? 'Retired'
            : 'Active'}
        </span>
      ),
    },
    {
      header: 'Usage',
      accessorKey: 'usageCount',
      cell: ({ row }) => getUsageLabel(row.original),
    },
    {
      header: 'Preview',
      accessorKey: 'imageUrl',
      cell: ({ row }) => (
        <img
          src={row.original.imageUrl}
          alt={getDisplayName(row.original)}
          style={{ width: '40px', height: '40px', objectFit: 'contain' }}
        />
      ),
    },
    {
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: ({ row }) => formatDisplayDate(row.original.createdAt),
    },
    {
      header: 'Updated At',
      accessorKey: 'updatedAt',
      cell: ({ row }) => formatDisplayDate(row.original.updatedAt),
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {hasPermission('icons_management_update') && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => handleOpenEditModal(row.original)}
              aria-label={`Edit ${getDisplayName(row.original)}`}
            >
              <KeenIcon type="filled" icon="pencil" className="text-gray-500" />
            </button>
          )}
          {hasPermission('icons_management_update') &&
            row.original.status === IconImageStatus.ACTIVE && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => handleLifecycleAction(row.original, 'retire')}
                aria-label={`Retire ${getDisplayName(row.original)}`}
              >
                Retire
              </button>
            )}
          {hasPermission('icons_management_update') &&
            row.original.status === IconImageStatus.RETIRED && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => handleLifecycleAction(row.original, 'reactivate')}
                aria-label={`Reactivate ${getDisplayName(row.original)}`}
              >
                Activate
              </button>
            )}
          {hasPermission('icons_management_delete') && row.original.canDelete && (
            <button
              type="button"
              className="btn btn-sm btn-light text-red-600"
              onClick={() => handleDeleteClick(row.original)}
              aria-label={`Delete ${getDisplayName(row.original)}`}
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle id="icon-images-management-heading" />
            <ToolbarDescription>
              You can manage your Icons here.
            </ToolbarDescription>
          </ToolbarHeading>
          {hasPermission('icons_management_create') && (
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleOpenCreateModal}
                aria-label="Add Icon"
                data-testid="add-icon-button"
              >
                Add Icon
              </button>
            </ToolbarActions>
          )}
        </Toolbar>
      </Container>
      <Container>
        <div className="card">
          <div className="card-body">
            {isLoading ? (
              <div
                className="text-center py-4"
                role="status"
                aria-live="polite"
              >
                Loading icon images...
              </div>
            ) : isError ? (
              <div className="text-center py-4 text-red-500" role="alert">
                Failed to load icon images. Please try again.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={iconImages?.data || []}
                rowSelect={false}
                loading={isLoading}
                pagination={{ page: 0, size: 10, more: false }}
                aria-label="Icon images table"
              />
            )}
          </div>
        </div>
      </Container>
      <IconImageModal
        open={modalState.isOpen}
        onClose={handleCloseModal}
        mode={modalState.mode}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        frozenName={modalState.image?.name}
      />
      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setImageToDelete(null);
        }}
        title="Delete Icon"
        message={`Permanently delete "${imageToDelete ? getDisplayName(imageToDelete) : ''}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        confirmButtonVariant="danger"
        disabled={isDeleting}
      />
    </>
  );
};

export { IconImagesListings };
